import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const MaslowPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Why it is named Maslow | Maslow NYC</title>
        <meta name="description" content="Abraham Maslow put sanitation at the base of the hierarchy of needs. Why a company building private restrooms in SoHo, New York bears his name." />
        <link rel="canonical" href="https://maslow.nyc/maslow" />
      </Helmet>

      <div className="min-h-screen bg-maslow-cream">
        {/* Back Button */}
        <div className="container mx-auto px-6 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-maslow-navy hover:text-maslow-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
        </div>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-6 py-16 max-w-4xl"
        >
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-maslow-navy mb-6">
            Abraham Maslow
          </h1>
          <p className="text-2xl text-[color-mix(in_srgb,var(--navy)_70%,transparent)] font-light mb-12">
            1908 – 1970
          </p>

          {/* The Story */}
          <div className="space-y-8 text-[color-mix(in_srgb,var(--navy)_90%,transparent)] leading-relaxed text-lg">
            <p className="text-xl font-light">
              Abraham Maslow was an American psychologist who revolutionized our understanding
              of human motivation and potential. Best known for his Hierarchy of Needs, Maslow
              argued that human beings are motivated by a series of needs, starting with the most
              basic physiological requirements.
            </p>

            <div className="bg-white/50 border-l-4 border-maslow-gold p-8 my-12">
              <p className="text-2xl font-serif italic text-maslow-navy">
                "What a man can be, he must be."
              </p>
              <p className="text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mt-2">
                — Abraham Maslow
              </p>
            </div>

            <h2 className="text-3xl font-serif font-bold text-maslow-navy mt-12 mb-6">
              The Hierarchy of Needs
            </h2>

            <p>
              Maslow's pyramid is simple yet profound. At the base are physiological needs:
              air, water, food, shelter, sleep—and yes, access to sanitation. Without these
              fundamentals, nothing else matters. You cannot pursue safety, love, esteem, or
              self-actualization if your most basic needs are unmet.
            </p>

            <p>
              In 1943, when Maslow published his groundbreaking theory, he identified these
              physiological needs as the foundation upon which all human flourishing depends.
              Yet here we are, more than eighty years later, in one of the wealthiest cities in human history,
              and that foundation remains fractured.
            </p>

            <h2 className="text-3xl font-serif font-bold text-maslow-navy mt-12 mb-6">
              The Missing Infrastructure
            </h2>

            <p>
              New York City has more than 8 million people and 1,064 public restrooms. Only 973
              of them work. That is one working restroom for every 9,000 people.
            </p>

            <p>
              When a nursing mother has nowhere to feed her child in privacy, when a person with
              Crohn's disease must map their entire day around restroom access, when someone in
              a wheelchair cannot find an accessible facility—these are failures at the base of Maslow's pyramid.
            </p>

            <h2 className="text-3xl font-serif font-bold text-maslow-navy mt-12 mb-6">
              Why I am building it
            </h2>

            <p>
              So Maslow is building them — no crusade, no charity drive, just rooms
              held to the standard this city already expects everywhere else.
              Premium is the floor, not an upgrade.
            </p>

            <p>
              I named this project after Abraham Maslow and I mean it literally.
              If the first tier of human needs includes sanitation, then the first tier of urban
              infrastructure must include restrooms. Everything else—the culture, the innovation,
              the community—rests on that foundation.
            </p>

            <p className="text-xl font-light italic text-maslow-navy mt-12">
              Dr. Maslow showed us the pyramid. I'm building the base.
            </p>

            <Link
              to="/rooms"
              className="text-maslow-gold hover:text-maslow-navy font-medium transition-colors inline-block mt-8"
            >
              The rooms I'm building →
            </Link>
          </div>

          {/* Visual Separator */}
          <div className="mt-16 mb-8 flex items-center justify-center">
            <div className="h-px bg-maslow-gold w-24"></div>
          </div>

          {/* Footer */}
          <div className="text-center mt-16 pb-16">
            <p className="text-sm uppercase tracking-widest text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mb-4">
              The Infrastructure of Dignity
            </p>
            <Link
              to="/"
              className="text-maslow-gold hover:text-maslow-navy font-medium transition-colors"
            >
              Return Home
            </Link>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default MaslowPage;
